import React, { useEffect, useState } from 'react';
import { ArrowDownLeft, Calendar, Download, TrendingDown } from 'lucide-react';

interface TrialBalanceReportProps {
  endDate: string;
  setEndDate: (date: string) => void;
  accounts: any[];
  calculateTrialBalance: () => any;
  setReportAccountId: (id: string | null) => void;
  setActiveReport: (report: string | null) => void;
  handleExport: () => void;
}

type TrialBalanceData = Record<string, { debit: number; credit: number }>;

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const normalizeTrialBalance = (payload: any): TrialBalanceData => {
  const report = payload?.trialBalance ?? payload?.accounts ?? payload?.data ?? payload?.balances ?? payload;
  const entries: Array<[string, any]> = Array.isArray(report)
    ? report.map((row: any, index: number) => [
        row.accountName ?? row.account?.name ?? row.name ?? row.account?.id ?? row.accountId ?? String(index),
        row,
      ])
    : Object.entries(report || {});

  return entries.reduce<TrialBalanceData>((normalized, [key, row]) => {
    const values = typeof row === 'object' && row !== null ? row : { balance: row };
    const debit = toNumber(values.debit ?? values.debitAmount ?? values.totalDebit);
    const credit = toNumber(values.credit ?? values.creditAmount ?? values.totalCredit);
    const balance = toNumber(values.balance ?? values.netBalance ?? values.netTotal);
    const name = values.accountName ?? values.account?.name ?? values.name ?? key;

    normalized[name] = {
      debit: debit ?? (balance !== null && balance > 0 ? balance : 0),
      credit: credit ?? (balance !== null && balance < 0 ? Math.abs(balance) : 0),
    };
    return normalized;
  }, {});
};

export const TrialBalanceReport: React.FC<TrialBalanceReportProps> = ({
  endDate,
  setEndDate,
  accounts,
  calculateTrialBalance,
  setReportAccountId,
  setActiveReport,
  handleExport
}) => {
  const [localTbData, setLocalTbData] = useState<TrialBalanceData | null>(null);
  const localToken = localStorage.getItem('app_auth_token');
  const orgId = localStorage.getItem('selected_org_id');

  useEffect(() => {
    if (!localToken || !orgId) {
      setLocalTbData(null);
      return;
    }

    const controller = new AbortController();
    const query = endDate ? `?asOf=${encodeURIComponent(endDate)}` : '';

    setLocalTbData(null);
    fetch(`/api/organizations/${orgId}/reports/trial-balance${query}`, {
      headers: { Authorization: `Bearer ${localToken}` },
      signal: controller.signal,
    })
      .then(async response => {
        if (!response.ok) throw new Error('Unable to load trial balance report');
        return response.json();
      })
      .then(payload => setLocalTbData(normalizeTrialBalance(payload)))
      .catch((error: any) => {
        if (error.name !== 'AbortError') setLocalTbData(null);
      });

    return () => controller.abort();
  }, [endDate, localToken, orgId]);

  const tbData = localTbData ?? (calculateTrialBalance() as TrialBalanceData);
  const totalDebit: number = Object.values(tbData).reduce((a: number, b: any) => a + (b.debit || 0), 0);
  const totalCredit: number = Object.values(tbData).reduce((a: number, b: any) => a + (b.credit || 0), 0);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <button 
          onClick={() => setActiveReport(null)}
          className="flex items-center gap-2 text-sm font-bold hover:opacity-70 transition-opacity"
        >
          <ArrowDownLeft className="w-4 h-4 rotate-45" />
          <span>Back to Reports</span>
        </button>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <Calendar className="w-4 h-4 opacity-50" />
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">As of</span>
            <input 
              type="date" 
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
          </div>
          <button 
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-[#141414] rounded-full text-xs font-bold hover:bg-[#141414] hover:text-[#E4E3E0] transition-all"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      <div className="bg-white/50 border border-[#141414] rounded-[40px] p-12 shadow-sm">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold tracking-tighter italic serif mb-2">Trial Balance</h1>
          <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40">
            As of {new Date(endDate).toLocaleDateString()}
          </p>
        </div>

        <table className="w-full">
          <thead>
            <tr className="text-left border-b-2 border-[#141414]">
              <th className="py-4 text-sm font-bold uppercase tracking-widest opacity-40">Account</th>
              <th className="py-4 text-sm font-bold uppercase tracking-widest opacity-40 text-right">Debit</th>
              <th className="py-4 text-sm font-bold uppercase tracking-widest opacity-40 text-right">Credit</th>
              <th className="py-4 text-sm font-bold uppercase tracking-widest opacity-40 text-right">Net Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#141414]/5">
            {Object.entries(tbData).map(([name, balances]: [string, any]) => {
              const account = accounts.find(a => a.name === name);
              return (
                <tr key={name} className="hover:bg-[#141414]/5 transition-colors">
                  <td className="py-4">
                    <button 
                      onClick={() => {
                        setActiveReport('Account Transactions');
                        if (account) setReportAccountId(account.id);
                      }}
                      className="font-bold hover:underline text-left"
                    >
                      {name}
                    </button>
                  </td>
                  <td className="py-4 font-mono text-right">
                    {balances.debit > 0 ? (
                      <button 
                        onClick={() => {
                          setActiveReport('Account Transactions');
                          if (account) setReportAccountId(account.id);
                        }}
                        className="hover:underline"
                      >
                        {balances.debit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </button>
                    ) : ''}
                  </td>
                  <td className="py-4 font-mono text-right">
                    {balances.credit > 0 ? (
                      <button 
                        onClick={() => {
                          setActiveReport('Account Transactions');
                          if (account) setReportAccountId(account.id);
                        }}
                        className="hover:underline"
                      >
                        {balances.credit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </button>
                    ) : ''}
                  </td>
                  <td className="py-4 font-mono text-right font-bold">
                     <button 
                       onClick={() => {
                         setActiveReport('Account Transactions');
                         if (account) setReportAccountId(account.id);
                       }}
                       className="hover:underline"
                     >
                       ${Math.abs(balances.debit - balances.credit).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                       <span className="text-[10px] ml-1 uppercase opacity-50">{(balances.debit - balances.credit) >= 0 ? 'Dr' : 'Cr'}</span>
                     </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-[#141414] bg-[#141414]/5">
              <td className="py-6 font-bold italic serif text-lg">Total</td>
              <td className="py-6 font-mono font-bold text-right text-base">
                <button 
                  onClick={() => {
                    setActiveReport('Account Transactions');
                    setReportAccountId(null);
                  }}
                  className="hover:underline"
                >
                  ${totalDebit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </button>
              </td>
              <td className="py-6 font-mono font-bold text-right text-base">
                <button 
                  onClick={() => {
                    setActiveReport('Account Transactions');
                    setReportAccountId(null);
                  }}
                  className="hover:underline"
                >
                  ${totalCredit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </button>
              </td>
              <td className="py-6 font-mono font-bold text-right text-base">
                <button 
                  onClick={() => {
                    setActiveReport('Account Transactions');
                    setReportAccountId(null);
                  }}
                  className="hover:underline"
                >
                  ${Math.abs(totalDebit - totalCredit).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  <span className="text-[10px] ml-1 uppercase opacity-50">{(totalDebit - totalCredit) >= 0 ? 'Dr' : 'Cr'}</span>
                </button>
              </td>
            </tr>
          </tfoot>
        </table>
        
        {Math.abs(totalDebit - totalCredit) > 0.01 && (
          <div className="mt-8 p-4 bg-red-100 border border-red-400 text-red-700 rounded-xl flex items-center gap-3">
            <TrendingDown className="w-5 h-5" />
            <p className="text-sm font-bold">Warning: Trial balance is not equal. Difference: ${(totalDebit - totalCredit).toLocaleString()}</p>
          </div>
        )}
      </div>
    </div>
  );
};
